import { complaintsFirstFor, type PortalConfigMeta } from "@/types/task-actions";

export interface WizardStep {
  key: string;
  label: string;
  /** Primary route for this step (used for top-bar active matching and navigation). */
  path: string;
  /** Additional route prefixes that belong to this step (for active-state matching). */
  childPaths?: string[];
  /** Whether this step appears in the top-bar navigation. */
  topNav: boolean;
}

/** Which case-history config roles are attached to the task (drives which history steps appear). */
export interface WizardConfigRoles {
  /** A non-general (typeId !== 1) portal config is attached — the "custom" history step. */
  hasCustomConfig: boolean;
  /** The canonical General History config (typeId === 1) is attached — rendered via the config renderer. */
  hasGeneralConfig: boolean;
}

/** Route that renders the General History portal config through the case-history wire renderer. */
export const GENERAL_HISTORY_CONFIG_PATH = "/complaint/general-history";

/** Family Health History page; its own step in the config flow, a General History sub-page in legacy. */
export const FAMILY_HISTORY_PATH = "/complaint/family-history";

/** Keys of the steps that make up the History group (everything between financial and Forms). */
export const HISTORY_STEP_KEYS: ReadonlySet<string> = new Set([
  "portal-config",
  "complaint-history",
  "general-history",
  "family-history",
]);

/**
 * Build the ordered wizard step list from portal config metadata.
 *
 * Top-bar always shows three items: **Demographics**, **History**, **Forms**.
 * Sub-steps (Comm Prefs, Financial, individual histories) are part of the flow
 * but do not get their own top-bar link.
 *
 * General History always renders as a portal config (TypeId === 1) via the wire renderer at
 * `GENERAL_HISTORY_CONFIG_PATH` — never the hardcoded health-history pages — so its answers reach
 * the visit summary and signed document. The hardcoded pages remain only as the legacy (no portal
 * config) fallback.
 *
 * Family Health History is part of General History: it is its own step directly after the General
 * step, shown whenever a General config is attached and never without one. Its questions are
 * optional, so a patient can continue past it without answering.
 *
 * @param meta  Parsed portal config metadata (null when no portal config — legacy NPH flow).
 * @param hasForms  Whether the patient has pending FORM tasks.
 * @param roles  Which config roles are attached; defaults derived from meta for backwards safety.
 */
export function buildWizardSteps(
  meta: PortalConfigMeta | null,
  hasForms: boolean,
  roles?: WizardConfigRoles,
  /** Device-remembered completion; completed leading steps are dropped on resume. */
  progress?: { demographics?: boolean; commPrefs?: boolean; financial?: boolean }
): WizardStep[] {
  const steps: WizardStep[] = [];
  const hasCustomConfig = roles?.hasCustomConfig ?? Boolean(meta);
  const hasGeneralConfig = roles?.hasGeneralConfig ?? (meta?.generalBehavior === "include");

  // --- Demographics group (top-nav item) ---
  if (!progress?.demographics) {
    steps.push({
      key: "demographics",
      label: "Demographics",
      path: "/complaint/demographics",
      childPaths: [
        "/complaint/communication-preferences",
        "/complaint/financial-responsibility",
        "/complaint/financial-responsibility-insurance",
        "/complaint/health-insurance",
        "/complaint/auto-insurance",
        "/complaint/workers-compensation",
      ],
      topNav: true,
    });
  }

  if (!progress?.commPrefs) {
    steps.push({
      key: "comm-prefs",
      label: "Communication Preferences",
      path: "/complaint/communication-preferences",
      topNav: false,
    });
  }

  if (!progress?.financial) {
    steps.push({
      key: "financial",
      label: "Financial Responsibility",
      path: "/complaint/financial-responsibility",
      // Own the insurance routes so findStepIndex still resolves them when the Demographics
      // step (which normally lists them) has been dropped on resume.
      childPaths: [
        "/complaint/financial-responsibility-insurance",
        "/complaint/health-insurance",
        "/complaint/auto-insurance",
        "/complaint/workers-compensation",
      ],
      topNav: false,
    });

    steps.push({
      key: "financial-insurance",
      label: "Insurance",
      path: "/complaint/financial-responsibility-insurance",
      topNav: false,
    });
  }

  // --- History group (top-nav item) ---
  const historyLabel = meta?.name || "History";

  const complaintStep: WizardStep = {
    key: "complaint-history",
    label: "Complaint History",
    path: "/complaint/primary-complaint",
    childPaths: [
      "/complaint/complaint-history",
      "/complaint/complaint-factors",
      "/complaint/complaint-summary",
      "/complaint/outcome-assesment",
    ],
    topNav: false,
  };

  /** General History rendered as the TypeId === 1 portal config (wire renderer), not the hardcoded pages. */
  const generalConfigStep: WizardStep = {
    key: "general-history",
    label: "General Health History",
    path: GENERAL_HISTORY_CONFIG_PATH,
    // top-nav History item only when there is no custom history step to own it.
    topNav: !hasCustomConfig,
  };

  const familyStep: WizardStep = {
    key: "family-history",
    label: "Family Health History",
    path: FAMILY_HISTORY_PATH,
    topNav: false,
  };

  if (meta || hasCustomConfig || hasGeneralConfig) {
    const customStep: WizardStep = {
      key: "portal-config",
      label: historyLabel,
      path: "/complaint/portal-config",
      topNav: true,
    };
    // Complaint inclusion is governed by complaintBehavior (defaults to "include"),
    // independent of whether a CUSTOM config is attached — a general-history-only
    // session must still ask complaints first unless explicitly set to "hide".
    const includeComplaint = (meta?.complaintBehavior ?? "include") !== "hide";
    const position = meta?.position ?? "before_complaint";

    const pushCustom = () => {
      if (hasCustomConfig) steps.push(customStep);
    };
    const pushComplaint = () => {
      if (includeComplaint) steps.push(complaintStep);
    };
    const pushGeneral = () => {
      if (!hasGeneralConfig) return;
      steps.push(generalConfigStep);
      steps.push(familyStep);
    };

    // Mirror the configurator's buildHistorySectionOrder so the patient intake order matches the setup's Position.
    if (!hasCustomConfig) {
      // General History drives (no custom config, so position has nothing to place): the template's
      // complaintsFirst puts complaints before General + Family (default) or after them.
      if (complaintsFirstFor(meta)) {
        pushComplaint();
        pushGeneral();
      } else {
        pushGeneral();
        pushComplaint();
      }
    } else if (position === "after_general") {
      // Complaint → General → Custom
      pushComplaint();
      pushGeneral();
      pushCustom();
    } else if (position === "after_complaint") {
      // Complaint → Custom → General
      pushComplaint();
      pushCustom();
      pushGeneral();
    } else {
      // before_complaint: Custom → Complaint → General
      pushCustom();
      pushComplaint();
      pushGeneral();
    }
  } else {
    // Legacy (no portal config) NPH flow: hardcoded complaint + health-history pages.
    steps.push({ ...complaintStep, topNav: true });
    steps.push({
      key: "general-history",
      label: "General Health History",
      path: "/complaint/health-history",
      childPaths: [
        "/complaint/health-history-2",
        "/complaint/health-history-3",
        FAMILY_HISTORY_PATH,
      ],
      topNav: false,
    });
  }

  // --- Forms (top-nav item, only if patient has forms) ---
  if (hasForms) {
    steps.push({
      key: "forms",
      label: "Forms",
      path: "/form-handler",
      topNav: true,
    });
  }

  return steps;
}

/** Completes an insurance-only (INS) task once the financial / insurance pages are done. */
export const INSURANCE_COMPLETE_PATH = "/complaint/insurance-complete";

/**
 * Steps for an insurance-only (INS) task: Demographics, Comm Prefs, then the financial /
 * insurance pages, then the completion step. No history and no forms (pending forms open from
 * the completion step). The financial steps stay even when the device remembers them as done:
 * insurance is what the clinic asked for. The completion step is what firstStepAfterFinancial
 * returns, so the insurance pages hand off to it without knowing about INS.
 */
export function buildInsuranceWizardSteps(
  progress?: { demographics?: boolean; commPrefs?: boolean }
): WizardStep[] {
  const steps = buildWizardSteps(null, false, { hasCustomConfig: false, hasGeneralConfig: false }, {
    demographics: progress?.demographics,
    commPrefs: progress?.commPrefs,
  }).filter((s) => !HISTORY_STEP_KEYS.has(s.key));
  steps.push({
    key: "insurance-complete",
    label: "Done",
    path: INSURANCE_COMPLETE_PATH,
    topNav: false,
  });
  return steps;
}

/** True when the planned steps are an insurance-only (INS) task's — no complaints are asked. */
export function isInsuranceOnlyFlow(steps: WizardStep[]): boolean {
  return steps.some((s) => s.path === INSURANCE_COMPLETE_PATH);
}

/** Find the index of the step that owns `pathname`. */
export function findStepIndex(steps: WizardStep[], pathname: string): number {
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    if (pathname === s.path || pathname.startsWith(s.path + "?") || pathname.startsWith(s.path + "/")) return i;
    if (s.childPaths) {
      for (const cp of s.childPaths) {
        if (pathname === cp || pathname.startsWith(cp + "?") || pathname.startsWith(cp + "/")) return i;
      }
    }
  }
  return -1;
}

/** Next step's primary path after the step that owns `currentPath`, or null. */
export function wizardNextPath(steps: WizardStep[], currentPath: string): string | null {
  const idx = findStepIndex(steps, currentPath);
  if (idx < 0 || idx >= steps.length - 1) return null;
  return steps[idx + 1].path;
}

/** Previous step's primary path before the step that owns `currentPath`, or null. */
export function wizardPrevPath(steps: WizardStep[], currentPath: string): string | null {
  const idx = findStepIndex(steps, currentPath);
  if (idx <= 0) return null;
  return steps[idx - 1].path;
}

/** True when another History-group step comes after the step that owns `currentPath`. */
export function hasLaterHistoryStep(steps: WizardStep[], currentPath: string): boolean {
  const idx = findStepIndex(steps, currentPath);
  if (idx < 0) return false;
  return steps.slice(idx + 1).some((s) => HISTORY_STEP_KEYS.has(s.key));
}

/** Steps that should appear in the top-bar navigation. */
export function topNavSteps(steps: WizardStep[]): WizardStep[] {
  return steps.filter((s) => s.topNav);
}

/**
 * Return the path of the first step *after* the financial sub-steps.
 * This is the step the financial / insurance pages should navigate to on "Continue."
 */
export function firstStepAfterFinancial(steps: WizardStep[]): string {
  const financialKeys = new Set(["demographics", "comm-prefs", "financial", "financial-insurance"]);
  const next = steps.find((s) => !financialKeys.has(s.key));
  return next?.path ?? "/";
}
